import React, { useMemo } from 'react';
import { useProCalculator } from '../hooks/useProCalculator';
import SectionHelp from './SectionHelp';
import ToggleSwitch from './ToggleSwitch';
import { getDefaultReference } from '../utils/defaultReferenceConfig';
import { getReferenceStatus } from '../utils/sectionCompletionUtils';
import '../styles/ReferenceSection.css';
import { useDebug } from '../contexts/DebugContext';

const ReferenceSection = () => {
  const {
    reference,
    defaultReference,
    useDefaultReference,
    setUseDefaultReference,
    handleReferenceChange,
    isMobile,
    goToNextSection,
    activeSection
  } = useProCalculator();
  const { isDebugMode, getDebugId } = useDebug();
  
  // FIXED UNITS
  const isMetric = false;
  const isMetricWeight = true;
  
  // Determine if the section is complete
  const referenceStatus = getReferenceStatus(reference);
  const isReferenceSectionComplete = useDefaultReference || referenceStatus === 'completed';

  // Calculate derived values using useMemo
  const calculatedValues = useMemo(() => {
    const width = parseFloat(reference.width);
    const length = parseFloat(reference.length);
    const weightNoIcing = parseFloat(reference.weightNoIcing);
    const weight = parseFloat(reference.weight);

    let icingWeight = NaN;
    let area = NaN;
    let icingPerArea = NaN;

    if (!isNaN(weight) && !isNaN(weightNoIcing) && weight >= weightNoIcing) {
      icingWeight = weight - weightNoIcing;
    }

    if (!isNaN(width) && !isNaN(length) && width > 0 && length > 0) {
      area = width * length;
    }

    if (!isNaN(icingWeight) && !isNaN(area) && area > 0) {
      icingPerArea = icingWeight / area;
    }

    return {
      icingWeight,
      area,
      icingPerArea
    };
  }, [reference.width, reference.length, reference.weightNoIcing, reference.weight]);

  const { icingWeight, area, icingPerArea } = calculatedValues;

  // Determine units - FIXED
  const weightUnit = 'g';
  const areaUnit = 'in²';
  const perAreaUnit = 'g/in²';

  // Handle default reference toggle
  const handleDefaultReferenceToggle = (isUsingDefault) => {
    console.log('Pro Calculator - Toggle default reference:', isUsingDefault);
    
    // Update the toggle state first
    setUseDefaultReference(isUsingDefault);
    
    if (isUsingDefault) {
      // Get default reference values based on FIXED unit settings
      const defaultRef = getDefaultReference(false, true); // isMetric=false, isMetricWeight=true
      console.log('Pro Calculator - Setting fixed default reference values (in/g):', defaultRef);
      
      // Create a new reference object with all values
      const newReference = {
        width: defaultRef.width,
        length: defaultRef.length,
        weightNoIcing: defaultRef.weightNoIcing,
        weight: defaultRef.weight
      };
      
      // Set all values at once with a single call to avoid multiple conversions
      Object.entries(newReference).forEach(([key, value]) => {
        handleReferenceChange(key, value);
      });
    }
  };
  
  return (
    <>
      <div className="reference-section">
        
        {/* Header with title only */}
        <div className="section-header"> 
          <h2 className="section-title">Reference Cookie</h2>
        </div>

        {/* Always visible Help text container */}
        <div className="section-help-container">
          <SectionHelp sectionId="reference" isMobile={isMobile} />
        </div>

        <div className="reference-toggle-container">
          <label className="reference-toggle-label">
            Use Default Reference Cookie
          </label>
          <ToggleSwitch
            isOn={useDefaultReference}
            onToggle={handleDefaultReferenceToggle}
          />
          {isDebugMode && 
            <span style={{ color: 'red', marginLeft: '5px', fontWeight: 'bold' }}>
              {getDebugId('pro-use-default-ref')}
            </span>
          }
        </div>
        
        <div className="reference-form">
          {/* Row 1: All four main input fields */}
          <div className="reference-field-row input-row"> 
            <div className="reference-field">
              <label>Width: <span className="unit-label">(in)</span></label>
              <input 
                id="pro-ref-width"
                type="number" 
                value={reference.width}
                onChange={(e) => handleReferenceChange('width', e.target.value)}
                disabled={useDefaultReference}
                className="reference-input"
                min="0"
                step="0.01"
              />
              {isDebugMode && 
                <span style={{ color: 'red', marginLeft: '5px', fontWeight: 'bold' }}>
                  {getDebugId('pro-ref-width')}
                </span>
              }
            </div>
            <div className="reference-field">
              <label>Length: <span className="unit-label">(in)</span></label>
              <input 
                id="pro-ref-length"
                type="number" 
                value={reference.length}
                onChange={(e) => handleReferenceChange('length', e.target.value)}
                disabled={useDefaultReference}
                className="reference-input"
                min="0"
                step="0.01"
              />
              {isDebugMode && 
                <span style={{ color: 'red', marginLeft: '5px', fontWeight: 'bold' }}>
                  {getDebugId('pro-ref-length')}
                </span>
              }
            </div>
            <div className="reference-field">
              <label>Weight without Icing: <span className="weight-unit">(g)</span></label>
              <input 
                id="pro-ref-weight-no-icing"
                type="number" 
                value={reference.weightNoIcing}
                onChange={(e) => handleReferenceChange('weightNoIcing', e.target.value)}
                disabled={useDefaultReference}
                className="reference-input"
                min="0"
                step="0.01"
              />
              {isDebugMode && 
                <span style={{ color: 'red', marginLeft: '5px', fontWeight: 'bold' }}>
                  {getDebugId('pro-ref-weight-no-icing')}
                </span>
              }
            </div>
            <div className="reference-field">
              <label>Weight with Icing: <span className="weight-unit">(g)</span></label>
              <input 
                id="pro-ref-weight"
                type="number" 
                value={reference.weight}
                onChange={(e) => handleReferenceChange('weight', e.target.value)}
                disabled={useDefaultReference}
                className="reference-input"
                min="0"
                step="0.01"
              />
              {isDebugMode && 
                <span style={{ color: 'red', marginLeft: '5px', fontWeight: 'bold' }}>
                  {getDebugId('pro-ref-weight')}
                </span>
              }
            </div>
          </div>
          
          {/* Row 2: Calculated Fields */}
          <div className="reference-field-row calculated-row">
            {/* Icing Used Field */}
            <div className="reference-field calculated-field">
              <label>Icing Used: <span className="weight-unit">(g)</span></label>
              <input
                type="text" // Use text to display formatted value
                value={!isNaN(icingWeight) ? icingWeight.toFixed(2) : '-'}
                readOnly
                disabled
                className="calculated-value-input" // Add class for styling
              />
            </div>
            
            {/* Icing per Square Inch Field */}
            <div className="reference-field calculated-field">
              <label>Icing per Square Inch: <span className="weight-unit">(g/in²)</span></label>
              <input
                type="text" // Use text to display formatted value
                value={!isNaN(icingPerArea) ? icingPerArea.toFixed(4) : '-'}
                readOnly
                disabled
                className="calculated-value-input" // Add class for styling
              />
            </div>
          </div>
        </div>
      </div>
      {activeSection !== 'all' && (
        <button
          className="next-section-button"
          onClick={() => goToNextSection('reference')}
          disabled={!isReferenceSectionComplete} 
          title={!isReferenceSectionComplete ? 'Please complete all reference fields' : 'Go to Cookie Names'}
        >
          Next: Cookie Names
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14"></path>
            <path d="M12 5l7 7-7 7"></path>
          </svg>
        </button>
      )}
    </>
  );
};

export default ReferenceSection;
